import React from 'react'
import classnames from 'classnames/bind'

import { Container } from '@caudena/core/ui-kit'
import Form, { Field, validators } from '@caudena/core/Form'
import { MagicGrid } from '@caudena/core/ui-kit'
import { Label } from '@caudena/core/ui-kit'
import { RadioButtonGroup } from '@caudena/core/ui-kit'
import { Switch } from '@caudena/core/ui-kit'
import { Checkbox } from '@caudena/core/ui-kit'

import RuleCategoryFilter from './RuleCategoryFilter'
import RuleSeverityFilter from './RuleSeverityFilter'
import RuleRiskAmountFilter from './RuleRiskAmountFilter'
import RuleExposureFilter from './RuleExposureFilter'
import CreateRuleConfirmationButton from '../CreateRuleConfirmationButton'

import { NewRuleApiParams } from '../../../../types/Rule'

import styles from './index.scss'

const cx = classnames.bind(styles)

const directionOptions = [
  { value: 'both', label: 'In / Out' },
  { value: 'in', label: 'Incoming' },
  { value: 'out', label: 'Outgoing' },
]

const initialFormValues = {
  type: 'exposure',
  direction: 'both',
  category: '',
  percentage: 0,
  riskAmount: '',
  severity: 'HIGH',
  autoBlock: false,
  retrospectivelyApply: false,
  retrospectivelyBlock: false,
  usd: false,
}

interface CreateExposureRuleFormProps {
  onSubmit: (data) => Promise<any>
  onSubmitModalOpen: (isOpen: boolean) => void
}

const CreateExposureRuleForm: React.FC<CreateExposureRuleFormProps> = ({
  onSubmit,
  onSubmitModalOpen,
}) => {
  const formRef = React.useRef<HTMLFormElement>(null)

  const handleSubmit = (data) => {
    const submitData: NewRuleApiParams = {
      type: data.type,
      severity: data.severity,
      name: data.category,
      direction: data.direction,
      percentage: data.percentage,
      usd: data.usd,
      usd_amount: data.riskAmount,
      block_client: data.autoBlock,
      run_retrospectivly: data.retrospectivelyApply,
      block_old_clients: data.retrospectivelyBlock,
    }

    return onSubmit(submitData).then(() => formRef.current?.form.restart())
  }

  React.useEffect(() => {
    const unsubscribe = formRef.current?.form.subscribe(
      ({ values: { retrospectivelyApply } }) => {
        if (!retrospectivelyApply) {
          formRef.current?.form.change('retrospectivelyBlock', false)
        }
      },
      { values: true }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialFormValues}
      ref={formRef as any}
    >
      {({ form }) => {
        const { values, valid } = form.getState()
        const { usd, retrospectivelyApply } = values

        return (
          <Container gap={[1, 0]}>
            <MagicGrid gap={2.5}>
              <Label content="Transaction direction">
                <Field
                  name="direction"
                  as={RadioButtonGroup}
                  options={directionOptions}
                  fullWidth
                />
              </Label>
              <Label content="Category (required)">
                <Field
                  name="category"
                  as={RuleCategoryFilter}
                  validate={validators.required()}
                />
              </Label>
              <Label content="Exposure (required)">
                <Field name="percentage" as={RuleExposureFilter} />
              </Label>
              <Label
                as={'div'}
                content={<Field as={Checkbox} name="usd" label="Risk amount" />}
              >
                <Field
                  name="riskAmount"
                  disabled={!usd}
                  validate={(value, allValues, meta) => {
                    if (allValues.usd)
                      return validators.required()(value, allValues, meta)
                  }}
                  as={RuleRiskAmountFilter}
                />
              </Label>
              <Label content="Severity">
                <Field name="severity" as={RuleSeverityFilter} />
              </Label>
              <Container gap={[1.5, 1.5]} className={cx('SwitchGroup')}>
                <MagicGrid>
                  <Field
                    name="autoBlock"
                    as={Switch}
                    type="single"
                    label="Automatically block client when the rule is triggered"
                  />
                  <Field
                    name="retrospectivelyApply"
                    as={Switch}
                    type="single"
                    label="Apply the rule retrospectively"
                  />
                  <Field
                    name="retrospectivelyBlock"
                    as={Switch}
                    type="single"
                    label="Block client when the rule is triggered in retrospect"
                    disabled={!retrospectivelyApply}
                  />
                </MagicGrid>
              </Container>
              <CreateRuleConfirmationButton
                onModalOpen={onSubmitModalOpen}
                values={values}
                valid={valid}
                submit={() => form.submit()}
              />
            </MagicGrid>
          </Container>
        )
      }}
    </Form>
  )
}

export default CreateExposureRuleForm
